import { feature, featureCollection } from '@turf/helpers'
import { GeoJsonLayer, IconLayer } from 'deck.gl'

import { colors } from '@weareberyl/design-system'

import { MODULES_MAP_all_zones_nodes as ZONE } from '~/components/MapView/__generated__/MODULES_MAP'
import { SERVICE_AREAS_GEOJSON } from '~/components/Scheme/withSchemeServiceAreas'

import {
  formatDatetime,
  getPositionFromCoordinates,
  getPositionFromLocation,
} from '~/utils'

import {
  BERYL_GREEN_PIN_COLOUR,
  BIKE_ICON,
  BRICK_PIN_COLOUR,
  EBIKE_ICON,
  JAFFA_PIN_COLOUR,
  LAKE_PIN_COLOUR,
  MODULE_ICON_MAPPING,
} from '~/components/Map/constants'
import {
  getZoneIcon,
  ZONE_ICON_SIZE,
  zoneIconLoadOptions,
} from '~/components/Map/zoneIcon'

import {
  FrameType,
  JobListQuery_table_JobConnection_nodes_Job as Job,
  ModuleHardware,
  JourneySetMapQuery_vehicles_VehicleConnection_nodes_Vehicle as Vehicle,
} from '~/gql/generated/graphql'
import { useJobTypes } from '~/hooks'
import locationPin from '~/images/location-pin.png'
import pinIconMap from '~/images/pins.png'
import spanner from '~/images/spanner.svg'

const VehicleIconPrefix = ModuleHardware

export type ServiceAreaGeoJsonLayerProps = {
  serviceAreas: SERVICE_AREAS_GEOJSON
  layerId: string
  layerProps?: object
}

const hexToRgb = (input: string) => {
  const m = input.match(/^#([0-9a-f]{6})$/i)
  if (m) {
    return [
      parseInt(m[1].substr(0, 2), 16),
      parseInt(m[1].substr(2, 2), 16),
      parseInt(m[1].substr(4, 2), 16),
    ]
  }

  throw new Error(`Invalid hex: {input}`)
}

const serviceAreaTypeIndex = {
  normal: 1,
  go_slow: 2,
  no_go: 3,
}

export const ServiceAreaGeoJsonLayer = ({
  serviceAreas,
  layerId,
  layerProps,
}: ServiceAreaGeoJsonLayerProps) => {
  // Create shallow copies of serviceAreas and serviceAreas.features
  // as both are read-only
  serviceAreas = { ...serviceAreas }
  serviceAreas.features = [...(serviceAreas.features || [])]
  // ensure that normal service areas are at the bottom of the layer
  serviceAreas.features = serviceAreas.features.sort((a, b) => {
    const aIndex = serviceAreaTypeIndex[a.properties.service_area_type] ?? 1
    const bIndex = serviceAreaTypeIndex[b.properties.service_area_type] ?? 1

    return aIndex - bIndex
  })

  return new GeoJsonLayer({
    id: layerId,
    data: serviceAreas,
    filled: true,
    pickable: true,
    opacity: 0.2,
    getFillColor: data => {
      if (data?.properties?.service_area_type === 'no_go') {
        // black
        return hexToRgb(colors.deepPurple)
      }

      if (data?.properties?.service_area_type === 'go_slow') {
        // yellow
        return hexToRgb(colors.jaffaPressed)
      }

      // none
      return [128, 0, 0, 0]
    },
    getLineColor: data => {
      if (data?.properties?.service_area_type === 'no_go') {
        // black
        return hexToRgb(colors.deepPurple)
      }

      if (data?.properties?.service_area_type === 'go_slow') {
        // yellow
        return hexToRgb(colors.jaffaPressed)
      }
      // red
      return hexToRgb(colors.alert)
    },
    lineWidthMinPixels: 2,
    getLineWidth: _ => 3,
    ...layerProps,
  })
}

type VehicleIconLayerProps = {
  vehicles: Vehicle[]
  layerId: string
  onHover?: (obj: object) => void
  layerProps?: object
  includeNonPresentModules?: boolean
}

export const VehicleIconLayer = ({
  vehicles,
  layerId,
  onHover,
  layerProps,
  includeNonPresentModules = false,
}: VehicleIconLayerProps) => {
  if (vehicles?.length <= 0) {
    return null
  }

  const frameTypeToIconPrefix = (frameType: FrameType) => {
    switch (frameType) {
      case FrameType.beryl_bike:
      case FrameType.pashley_tfl:
      case FrameType.pashley_westmidlands_bike:
        return VehicleIconPrefix.beryl_bike
      case FrameType.beryl_e_bike:
      case FrameType.beryl_e_bike_2:
      case FrameType.pashley_westmidlands_e_bike:
      case FrameType.segway_e_bike:
      case FrameType.segway_e_bike_b200:
      case FrameType.raleigh_cargo_e_bike:
        return VehicleIconPrefix.bbe
      case FrameType.okai_scooter:
        return VehicleIconPrefix.scooter
      default:
        return VehicleIconPrefix.beryl_bike
    }
  }

  const getIcon = (vehicle: Vehicle) => {
    const iconPrefix = frameTypeToIconPrefix(vehicle?.frame_type as FrameType)

    const hasReward = vehicle?.rewards?.length > 0
    let icon = `${iconPrefix}-jade`

    const message =
      vehicle?.detail?.module?.lock_state?.vehicle_lock_state ??
      vehicle?.detail?.module?.lock_state?.overall_state
    if (message?.includes('unlocked')) {
      icon = `${iconPrefix}-jaffa`
    }
    if (hasReward) {
      icon = `${icon}-promotional`
    }
    return icon
  }

  const getSize = (vehicle: Vehicle) => {
    const scale = 1.6 / 3
    if (
      frameTypeToIconPrefix(vehicle?.frame_type as FrameType) ===
      VehicleIconPrefix.bbe
    ) {
      return EBIKE_ICON.height * scale
    }
    return BIKE_ICON.height * scale
  }

  const transformIconProperties = (vehicle: Vehicle) => {
    const location = vehicle?.detail?.module?.location
    if (location) {
      const position = getPositionFromLocation(location)

      return {
        properties: {
          id: vehicle?.id,
          position,
          max_error: location?.max_error,
          time: formatDatetime(location?.time),
        },
      }
    }
  }

  return new IconLayer({
    id: layerId,
    iconAtlas: pinIconMap,
    pickable: true,
    data: vehicles.filter(
      v =>
        v?.detail?.module?.location?.longitude &&
        (includeNonPresentModules || v?.detail?.module?.is_present),
    ),
    getSize,
    iconMapping: MODULE_ICON_MAPPING,
    getIcon,
    getPosition: v => [
      v?.detail?.module?.location?.longitude,
      v?.detail?.module?.location?.latitude,
    ],
    autoHighlight: true,
    highlightColor: [0, 0, 0, 32],
    onHover: !onHover
      ? null
      : args => {
          return onHover({
            ...args,
            object: transformIconProperties(args.object),
          })
        },
    ...layerProps,
  })
}

const formatZoneFeatures = (zoneData: ZONE[]) => {
  const features = zoneData?.map(
    ({
      id,
      name,
      geom,
      centre,
      bikes,
      ebikes,
      scooters,
      capacity,
      is_suspended,
      zone_type,
    }) => {
      const properties = {
        id,
        name,
        centre,
        // `bikes` includes ebikes (they both have propulsion_type: human), deduct them to show pedal bikes
        bikes: bikes - ebikes,
        ebikes,
        scooters,
        capacity,
        is_suspended,
        zone_type,
      }

      return feature(geom, properties)
    },
  )

  return featureCollection(features)
}

const transformZoneProperties = (zone: ZONE) => {
  if (!zone) return null
  const coords = zone?.centre?.coordinates
  const position = getPositionFromCoordinates(coords)

  return {
    properties: {
      id: zone?.id,
      name: zone?.name,
      position,
      bikes: zone?.bikes - zone?.ebikes,
      ebikes: zone?.ebikes,
      scooters: zone?.scooters,
      capacity: zone?.capacity,
      is_suspended: zone?.is_suspended,
    },
  }
}

type ZoneIconLayersProps = {
  zones: ZONE[]
  layerId: string
  onHover?: (obj: object) => void
  onClick?: (obj: object) => void
  iconLayerProps?: object
}

export const WorkshopIconLayers = ({
  zones: zoneData,
  layerId,
  onHover,
  iconLayerProps,
}: ZoneIconLayersProps) => {
  if (zoneData.length <= 0) {
    return []
  }
  const zoneFeatures = formatZoneFeatures(zoneData)
  const zones = zoneFeatures
    ? zoneFeatures?.features?.map(f => f.properties)
    : []
  const workshopIconLayer = new IconLayer({
    id: `workshop-pin-layer-${layerId}`,
    pickable: true,
    data: zones?.map((zone, index) => ({
      zone,
      id: `workshop-pin-${index}`,
      location: {
        latitude: zone.centre.coordinates[1],
        longitude: zone.centre.coordinates[0],
      },
    })),
    getSize: 33,
    getIcon: () => ({
      url: spanner,
      height: 150,
      width: 150,
      id: 'spanner',
      mask: true,
    }),
    getColor: () => [238, 149, 0],
    autoHighlight: true,
    highlightColor: [0, 0, 0, 32],
    getPosition: ({ location: l }) => [l.longitude, l.latitude],
    onHover: !onHover
      ? null
      : args => {
          return onHover({
            ...args,
            object: transformZoneProperties(args.object),
          })
        },
    ...iconLayerProps,
  })

  return [workshopIconLayer]
}

// Draw order within the zone icon layer: suspended bays at the bottom, then
// bays with fewer vehicles, so the busiest bays end up on top
const compareZoneStacking = (a: ZONE, b: ZONE) => {
  if (a.is_suspended !== b.is_suspended) {
    return a.is_suspended ? -1 : 1
  }
  return a.bikes + a.ebikes + a.scooters - (b.bikes + b.ebikes + b.scooters)
}

export const ZoneIconLayers = ({
  zones: zoneData,
  layerId,
  onHover,
  iconLayerProps,
}: ZoneIconLayersProps) => {
  const zoneFeatures = formatZoneFeatures(zoneData)
  // With no zones, this still returns the layer, just with no data. Removing
  // the layer would throw away its icon atlas, so every icon would be drawn
  // again when zones come back, e.g. after reloading when the map moves.
  const zones =
    zoneFeatures.features?.map(f => f.properties).sort(compareZoneStacking) ??
    []

  // Each zone's icon and counts are drawn as one image (see zoneIcon.ts), so
  // a single layer drawn in data order stacks them correctly. No depth test,
  // so later zones always draw over earlier ones.
  const zoneIconLayer = new IconLayer({
    id: 'icon-' + layerId,
    pickable: true,
    data: zones,
    getSize: ZONE_ICON_SIZE,
    getIcon: getZoneIcon,
    loadOptions: zoneIconLoadOptions,
    getPosition: zone => zone.centre.coordinates,
    parameters: {
      depthTest: false,
    },
    autoHighlight: true,
    highlightColor: [0, 0, 0, 32],
    getColor: z => (z.is_suspended ? [0, 0, 0, 190] : [0, 0, 0, 255]),
    onHover: !onHover
      ? null
      : args => {
          return onHover({
            ...args,
            object: transformZoneProperties(args.object),
          })
        },
    ...iconLayerProps,
  })

  return [zoneIconLayer]
}

type ZonePolygonLayerProps = {
  zones: ZONE[]
  layerId: string
  visible: boolean
  visibleZoneId?: string | null
}

export const ZonePolygonLayer = ({
  zones: zoneData,
  layerId,
  visible,
  visibleZoneId,
}: ZonePolygonLayerProps) => {
  const zones = formatZoneFeatures(zoneData)

  return new GeoJsonLayer({
    id: layerId,
    visible,
    // Only show 1 zone if visibleZoneId is provided, if null then show none, if undefined show all
    data:
      visibleZoneId || visibleZoneId === null
        ? zones.features.filter(
            ({ properties }) => properties.id === visibleZoneId,
          )
        : zones,
    opacity: 0.4,
    stroked: true,
    filled: true,
    lineWidthMinPixels: 2,
    getRadius: 5,
    getLineWidth: 1,
    lineJointRounded: true,
    getLineColor: [255, 164, 14],
    getFillColor: [255, 164, 14],
  })
}

export const WorkshopPolygonLayer = ({
  zones: zoneData,
  layerId,
  visible,
  onClick,
}: ZonePolygonLayerProps & { onClick: (obj) => void }) => {
  const zones = formatZoneFeatures(zoneData)

  return new GeoJsonLayer({
    id: layerId,
    visible,
    pickable: true,
    data: zones,
    opacity: 0.4,
    stroked: true,
    filled: true,
    lineWidthMinPixels: 2,
    getRadius: 5,
    getLineWidth: 1,
    lineJointRounded: true,
    getLineColor: [238, 149, 0],
    getFillColor: [82, 82, 92],
    autoHighlight: true,
    highlightColor: [0, 0, 0, 32],
    onClick,
  })
}

export const GeocoderPin = (location: {
  latitude: number
  longitude: number
}) => {
  return new IconLayer({
    id: 'geocoder-pin-layer',
    iconAtlas: locationPin,
    pickable: false,
    data: [
      {
        id: 'geocoder-pin',
        location,
      },
    ],
    getSize: 66,
    iconMapping: {
      marker: { x: 0, y: 0, width: 42, height: 60, anchorY: 60, mask: true },
    },
    getIcon: () => 'marker',
    getColor: () => [26, 152, 255], // lake
    getPosition: ({ location: l }) => [l.longitude, l.latitude],
    onHover: null,
  })
}

type JobIconLayerProps = {
  jobs: Array<Job>
  layerId: string
  onClick?: (obj: Record<string, unknown>) => void
  layerProps?: Record<string, unknown>
}

const getColor = (job: Job, jobCode: string) => {
  // Match colouring to Eng App.
  let pinColor
  const priority = job?.details?.priority === 'true'

  // override pin colour for battery swap jobs
  switch (jobCode) {
    case 'e-Scooter Battery Swap':
      pinColor = LAKE_PIN_COLOUR
      break
    case 'e-Bike Battery Swap':
      pinColor = JAFFA_PIN_COLOUR
      break
    default:
      pinColor = priority ? BRICK_PIN_COLOUR : BERYL_GREEN_PIN_COLOUR
  }

  return pinColor
}

type Coordinates = [number, number]
const getCoordinates = (job: Job): Coordinates | null => {
  if (!job) return null
  if (
    job.vehicle &&
    job.vehicle?.detail?.module?.location?.longitude &&
    job.vehicle?.detail?.module?.location?.latitude
  ) {
    return [
      job.vehicle.detail.module.location.longitude,
      job.vehicle.detail.module.location.latitude,
    ]
  }

  if (
    typeof job.details?.location === 'string' &&
    job.details?.location?.length > 0
  ) {
    return [
      Number(job.details.location.split(',')[1]),
      Number(job.details.location.split(',')[0]),
    ]
  }

  return null
}

export const JobIconLayer = ({
  jobs,
  layerId,
  onClick,
  layerProps,
}: JobIconLayerProps) => {
  const { jobCodeLookup } = useJobTypes()

  if (jobs?.length <= 0) return null

  return new IconLayer({
    id: layerId,
    iconAtlas: locationPin,
    iconMapping: {
      marker: { x: 0, y: 0, width: 42, height: 60, anchorY: 60, mask: true },
    },
    getSize: 33,
    pickable: true,
    data: jobs.filter(job => getCoordinates(job) !== null),
    getIcon: () => 'marker',
    getColor: job => getColor(job, jobCodeLookup?.[job.details.taskId]),
    getPosition: job => getCoordinates(job),
    autoHighlight: true,
    highlightColor: [0, 0, 0, 32],
    onClick,
    ...layerProps,
  })
}
